import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

interface AccordionContextValue {
  openItems: Set<string>;
  onToggle: (value: string) => void;
}

const AccordionContext = React.createContext<AccordionContextValue | undefined>(undefined);

function useAccordion() {
  const context = React.useContext(AccordionContext);
  if (!context) {
    throw new Error('useAccordion must be used within an Accordion');
  }
  return context;
}

interface AccordionProps {
  children: React.ReactNode;
  className?: string;
  type?: 'single' | 'multiple';
  defaultValue?: string | string[];
  onValueChange?: (openValues: string[]) => void;
}

export function Accordion({ 
  children, 
  className,
  type = 'multiple',
  defaultValue = [],
  onValueChange
}: AccordionProps) {
  const [openItems, setOpenItems] = React.useState<Set<string>>(() => {
    if (Array.isArray(defaultValue)) {
      return new Set(defaultValue);
    }
    if (typeof defaultValue === 'string') {
      return new Set([defaultValue]);
    }
    return new Set();
  });

  const onToggle = React.useCallback((value: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        if (type === 'single') {
          next.clear();
        }
        next.add(value);
      }
      // Call onValueChange callback with array of open values
      onValueChange?.(Array.from(next));
      return next;
    });
  }, [type, onValueChange]);

  return (
    <AccordionContext.Provider value={{ openItems, onToggle }}>
      <div className={cn('space-y-2', className)}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

interface AccordionItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function AccordionItem({ value, children, className }: AccordionItemProps) {
  return (
    <div className={cn('border rounded-lg', className)}>
      {children}
    </div>
  );
}

interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  children: React.ReactNode;
}

export function AccordionTrigger({ value, children, className, onClick, ...props }: AccordionTriggerProps) {
  const { openItems, onToggle } = useAccordion();
  const isOpen = openItems.has(value);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onToggle(value);
    onClick?.(e);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex w-full items-center justify-between p-4 text-left font-medium transition-all hover:bg-muted/50',
        className
      )}
      data-state={isOpen ? 'open' : 'closed'}
      {...props}
    >
      {children}
      <ChevronDown 
        className={cn(
          'h-4 w-4 shrink-0 transition-transform duration-200',
          isOpen && 'rotate-180'
        )} 
      />
    </button>
  );
}

interface AccordionContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function AccordionContent({ value, children, className }: AccordionContentProps) {
  const { openItems } = useAccordion();
  const isOpen = openItems.has(value);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number | 'auto'>('auto');

  React.useEffect(() => {
    if (contentRef.current) {
      if (isOpen) {
        setHeight(contentRef.current.scrollHeight);
      } else {
        setHeight(0);
      }
    }
  }, [isOpen, children]);

  if (!isOpen && height === 0) {
    return null;
  }

  return (
    <div
      ref={contentRef}
      className={cn(
        'overflow-hidden transition-all duration-200 ease-out',
        className
      )}
      style={{
        height: isOpen ? height : 0,
        opacity: isOpen ? 1 : 0,
      }}
    >
      <div className="p-4 pt-0">
        {children}
      </div>
    </div>
  );
}
